import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";

const SECTORS = ["Real Estate", "Legal & Corporate", "Finance & Investment", "Marketing & Brand", "Technology & Digital", "Operations", "Other"];
const CLIENT_TYPES = ["Developer", "HNW Investor", "Corporate", "Government", "Individual", "Family Office", "Startup"];

export default function OpportunityNew() {
  const nav = useNavigate();
  const [f, setF] = useState({
    title: "",
    description: "",
    sectors_involved: [],
    client_type: "",
    estimated_value_min: "",
    estimated_value_max: "",
    skills_needed: [],
  });
  const [skillInput, setSkillInput] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const toggleSector = (s) => {
    setF((p) => ({
      ...p,
      sectors_involved: p.sectors_involved.includes(s)
        ? p.sectors_involved.filter((x) => x !== s)
        : [...p.sectors_involved, s],
    }));
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setF((p) => ({ ...p, skills_needed: [...p.skills_needed, skillInput.trim()] }));
      setSkillInput("");
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        estimated_value_min: f.estimated_value_min ? Number(f.estimated_value_min) : null,
        estimated_value_max: f.estimated_value_max ? Number(f.estimated_value_max) : null,
      };
      const { data } = await api.post("/opportunities", payload);
      nav(`/opportunities/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="opp-new-page">
      <Link to="/opportunities" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9A9590] hover:text-[#C9A84C] transition">
        <ArrowLeft size={14} /> Back to pipeline
      </Link>

      <div>
        <div className="overline">New entry</div>
        <h1 className="font-serif text-4xl text-[#F0EDE8] mt-2 tracking-tight">Post an Opportunity</h1>
        <p className="text-[#9A9590] mt-3 max-w-xl">Share what you spotted. The right members will raise their hand.</p>
      </div>

      <form onSubmit={submit} className="space-y-7 ts-card p-8" data-testid="opp-form">
        <div>
          <label className="overline">Title</label>
          <input
            className="ts-input"
            placeholder="e.g. Luxury residential project — Palm Jumeirah"
            value={f.title}
            onChange={(e) => setF({ ...f, title: e.target.value })}
            required
            data-testid="opp-title"
          />
        </div>
        <div>
          <label className="overline">Description</label>
          <textarea
            className="ts-input min-h-[120px] py-3 leading-relaxed resize-none"
            placeholder="Context, client situation, what's needed from the group…"
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
            data-testid="opp-description"
          />
        </div>

        <div>
          <label className="overline">Sectors involved</label>
          <div className="flex flex-wrap gap-2 mt-3">
            {SECTORS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => toggleSector(s)}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
                  f.sectors_involved.includes(s)
                    ? "bg-[#C9A84C] text-[#0D0D0D]"
                    : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="overline">Client type</label>
          <div className="flex flex-wrap gap-2 mt-3">
            {CLIENT_TYPES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setF({ ...f, client_type: c })}
                className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
                  f.client_type === c
                    ? "bg-[#C9A84C] text-[#0D0D0D]"
                    : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="overline">Min value (AED)</label>
            <input
              type="number"
              className="ts-input"
              placeholder="500000"
              value={f.estimated_value_min}
              onChange={(e) => setF({ ...f, estimated_value_min: e.target.value })}
              data-testid="opp-min"
            />
          </div>
          <div>
            <label className="overline">Max value (AED)</label>
            <input
              type="number"
              className="ts-input"
              placeholder="2000000"
              value={f.estimated_value_max}
              onChange={(e) => setF({ ...f, estimated_value_max: e.target.value })}
              data-testid="opp-max"
            />
          </div>
        </div>

        <div>
          <label className="overline">Skills / sectors needed</label>
          <div className="flex gap-2 mt-3">
            <input
              className="ts-input flex-1"
              placeholder="Add a skill and press Enter"
              value={skillInput}
              onChange={(e) => setSkillInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSkill(); } }}
              data-testid="opp-skill-input"
            />
            <button type="button" onClick={addSkill} className="btn-ghost-gold px-4 rounded-md text-sm">Add</button>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {f.skills_needed.map((s) => (
              <span key={s} className="text-[10px] uppercase tracking-widest text-[#C9A84C] border border-[#C9A84C]/30 rounded-full px-3 py-1">
                {s}
              </span>
            ))}
          </div>
        </div>

        {error && <div className="text-sm text-red-400/90 border-l-2 border-red-500/60 pl-3 py-1">{error}</div>}

        <button type="submit" disabled={submitting} className="btn-gold w-full py-3 rounded-md disabled:opacity-70" data-testid="opp-submit">
          {submitting ? "Posting…" : "Post to the table"}
        </button>
      </form>
    </div>
  );
}
